import { useNavigate } from 'react-router-dom';
import config from 'src/shared/config';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { backofficeUrl } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import type { ZakekeEnvironment } from './__generated__/dashboardShellQuery.graphql';
import { useDashboardEnvironment } from './dashboardEnvironment';

export const SUBSCRIPTIONS_URL = backofficeUrl('/User/?tab=subscriptions');

/** The Usage tab of the account page, and the three sections it opens on. */
export const USAGE_URL = backofficeUrl('/User/?tab=usage');
export const AI_USAGE_URL = `${USAGE_URL}&section=ai`;
export const STORAGE_USAGE_URL = `${USAGE_URL}&section=storage`;
export const ADDON_USAGE_URL = `${USAGE_URL}&section=addons`;

/** The plans page (ChoosePlan), not the subscription tab of the account page. */
export const PLANS_URL = backofficeUrl('/seller/subscription');

/** The new Configurator backoffice: the Scene Editor, and with it a configurable product from scratch, sits behind it. */
const useIsNewConfiguratorBackoffice = () => {
    const { userInfo } = useAccountData();
    return useFeatureFlagWithConditions('newConfiguratorBackoffice', { userId: userInfo.userID.toString() });
};

/**
 * Whether the seller can build a product of this solution without a store. The Customizer always can; the
 * Configurator only behind `newConfiguratorBackoffice`, because the wizard builds it in the Scene Editor.
 */
export const useCanBuildFromScratch = (): boolean => {
    const { environment } = useDashboardEnvironment();
    const isNewConfiguratorBackoffice = useIsNewConfiguratorBackoffice();
    return environment !== 'COMPOSER' || isNewConfiguratorBackoffice;
};

/**
 * Where "add a product" leads. The wizard offers From Store and From Scratch to both solutions, and opens on
 * From Scratch when asked. Without the flag the Configurator has no from-scratch road, so it keeps going
 * straight to the store catalogue, as it did before the wizard learnt it.
 */
export const useAddProductUrl = (options: { fromScratch?: boolean } = {}): string => {
    const canBuildFromScratch = useCanBuildFromScratch();
    if (!canBuildFromScratch) return backofficeUrl('/product/connect-catalog');
    return options.fromScratch ? `${backofficeUrl('/product/add-product')}?source=scratch` : backofficeUrl('/product/add-product');
};

/**
 * Opens a product's setup the way the products list does (`handleEdit` in `productList.tsx`). A Viewer product lives in
 * the DAM gallery. While `newConfiguratorBackoffice` is off, a Configurator product is edited in the legacy Composer,
 * which lives outside this app; with it on, `/product` opens it on the Scene Editor. The dashboard lists the products
 * of every solution, so the decision follows the product's own environment.
 */
export const useOpenProductSetup = () => {
    const navigate = useNavigate();
    const isNewConfiguratorBackoffice = useIsNewConfiguratorBackoffice();

    return (productId: string | undefined, productEnvironment: ZakekeEnvironment | undefined, returnTo?: string) => {
        if (!productId) {
            navigate(backofficeUrl('/products'));
            return;
        }

        if (productEnvironment === 'VIEWER') {
            window.location.href = `/${config.culture}/Admin/DAM?page=/gallery`;
            return;
        }

        if (productEnvironment === 'COMPOSER' && !isNewConfiguratorBackoffice) {
            window.location.href = `/${config.culture}/Composer?page=product&id=${productId}`;
            return;
        }

        const returnParam = returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : '';
        navigate(`${backofficeUrl(`/product?product_id=${productId}`)}${returnParam}`);
    };
};
